/**
 * Utility functions for formatting chat-related data
 */

/**
 * Formats a timestamp for display in chat messages
 * - If today: Shows time only (e.g., "2:45 PM")
 * - If past: Shows date and time (e.g., "Jan 15, 2:45 PM")
 *
 * @param timestamp - ISO 8601 timestamp string or Date object
 * @returns Formatted timestamp string
 *
 * @example
 * formatTimestamp('2024-01-15T14:45:00Z') // "Jan 15, 2:45 PM" (if not today)
 * formatTimestamp(new Date().toISOString()) // "2:45 PM" (if today)
 */
export const formatTimestamp = (timestamp: string | Date): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Formats a timestamp as relative time for tooltips and detailed views
 * - Just now (< 1 minute)
 * - X minutes ago (< 60 minutes)
 * - X hours ago (< 24 hours)
 * - X days ago (< 7 days)
 * - Full date (>= 7 days)
 *
 * @param timestamp - ISO 8601 timestamp string or Date object
 * @returns Formatted relative time string
 *
 * @example
 * formatRelativeTime(new Date()) // "Just now"
 * formatRelativeTime(new Date(Date.now() - 5 * 60 * 1000)) // "5 minutes ago"
 * formatRelativeTime(new Date(Date.now() - 2 * 60 * 60 * 1000)) // "2 hours ago"
 */
export const formatRelativeTime = (timestamp: string | Date): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return 'Just now';
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }

  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  // For older messages, show full date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Formats a timestamp for use in message grouping
 * Groups messages by: "Today", "Yesterday", or date
 *
 * @param timestamp - ISO 8601 timestamp string or Date object
 * @returns Group label string
 *
 * @example
 * formatMessageGroupLabel(new Date()) // "Today"
 * formatMessageGroupLabel(new Date(Date.now() - 24 * 60 * 60 * 1000)) // "Yesterday"
 * formatMessageGroupLabel('2024-01-15T14:45:00Z') // "January 15, 2024"
 */
export const formatMessageGroupLabel = (timestamp: string | Date): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();

  // Check if today
  if (date.toDateString() === now.toDateString()) {
    return 'Today';
  }

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }

  // Show full date
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Gets a short timestamp for compact displays (e.g., message lists)
 *
 * @param timestamp - ISO 8601 timestamp string or Date object
 * @returns Short timestamp string
 *
 * @example
 * formatShortTimestamp(new Date()) // "2:45 PM" (if today)
 * formatShortTimestamp('2024-01-15T14:45:00Z') // "Jan 15" (if not today)
 */
export const formatShortTimestamp = (timestamp: string | Date): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};
